import { collection, query, where, getDocs, writeBatch, doc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Registration } from '../types';

export interface BibAssignmentRow {
  registrationId: string;
  registrationNumber?: number;
  firstName: string;
  lastName: string;
  email: string;
  raceDistance: string;
  bib: number | null;
  status: string;
}

/**
 * Convert a bib value to a positive integer. Empty values mean unassigned;
 * non-empty invalid values are rejected by the admin panel and save service.
 */
export const parseBibValue = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string' && value.trim() === '') return null;

  const text = String(value).trim();
  if (!/^\d+$/.test(text)) return null;

  const bib = Number(text);
  return Number.isSafeInteger(bib) && bib > 0 ? bib : null;
};

export const getBibAssignments = async (
  editionId: string,
  classFilter?: string[]
): Promise<BibAssignmentRow[]> => {
  const q = query(
    collection(db, 'registrations'),
    where('editionId', '==', editionId)
  );
  const snapshot = await getDocs(q);

  return snapshot.docs
    .map((docSnap) => {
      const data = docSnap.data() as Registration;
      return {
        registrationId: docSnap.id,
        registrationNumber: data.registrationNumber ?? undefined,
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        email: data.email || '',
        raceDistance: data.raceDistance || '',
        bib: parseBibValue(data.bib),
        status: data.status || '',
      };
    })
    // Filter by class if classFilter is provided
    .filter((row) => {
      if (classFilter && classFilter.length > 0) {
        return classFilter.includes(row.raceDistance);
      }
      // When no classFilter provided, return ALL registrations
      return true;
    })
    .sort((a, b) => (a.registrationNumber ?? 999999) - (b.registrationNumber ?? 999999));
};

export interface BibSaveEntry {
  registrationId: string;
  bib: number | null;
}

export const saveBibAssignments = async (
  editionId: string,
  assignments: BibSaveEntry[]
): Promise<void> => {
  // Re-read the complete event immediately before writing. This catches
  // duplicates in cancelled/hidden registrations and stale admin panels.
  const existingSnapshot = await getDocs(query(
    collection(db, 'registrations'),
    where('editionId', '==', editionId)
  ));
  const assignmentById = new Map(assignments.map((entry) => [entry.registrationId, entry]));
  const ownersByBib = new Map<number, string>();

  // Preserve registrations that are not part of this save operation.
  existingSnapshot.docs.forEach((docSnap) => {
    if (assignmentById.has(docSnap.id)) return;
    const bib = parseBibValue(docSnap.data().bib);
    if (bib === null) return;
    const previousOwner = ownersByBib.get(bib);
    if (previousOwner) {
      throw new Error(`Bib ${bib} is already assigned to multiple registrations in ${editionId}.`);
    }
    ownersByBib.set(bib, docSnap.id);
  });

  // Validate the complete proposed state, including all assignments in this save.
  assignments.forEach(({ registrationId, bib }) => {
    if (bib === null) return;
    const normalizedBib = parseBibValue(bib);
    if (normalizedBib === null) {
      throw new Error(`Bib for registration ${registrationId} must be a positive integer or empty.`);
    }
    const previousOwner = ownersByBib.get(normalizedBib);
    if (previousOwner && previousOwner !== registrationId) {
      throw new Error(`Bib ${normalizedBib} is already assigned to another registration in ${editionId}.`);
    }
    ownersByBib.set(normalizedBib, registrationId);
  });

  const BATCH_SIZE = 450;
  for (let i = 0; i < assignments.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    const chunk = assignments.slice(i, i + BATCH_SIZE);
    for (const { registrationId, bib } of chunk) {
      const ref = doc(db, 'registrations', registrationId);
      batch.update(ref, { bib: bib ?? null });
    }
    await batch.commit();
  }
};

export const autoAssignBibs = (
  rows: BibAssignmentRow[],
  startBib: number
): BibAssignmentRow[] => {
  let next = startBib;
  return rows.map((row) => ({ ...row, bib: next++ }));
};
